import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Search, Building2, Loader2, ScanLine } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import ListinoOcrDialog from '@/components/ListinoOcrDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Etichette per la fonte del lookup P.IVA (riusa stesso pattern di ClientiPage)
const PIVA_SOURCE_LABELS = { openapi: 'Registro Imprese', VIES: 'VIES', database: 'archivio locale' };
const getPivaSourceLabel = (source) => PIVA_SOURCE_LABELS[source] || 'archivio';

const EMPTY = {
  ragione_sociale: '', partita_iva: '', codice_fiscale: '',
  indirizzo: '', citta: '', cap: '', provincia: '',
  email: '', pec: '', telefono: '', codice_sdi: '',
  iban: '', referente: '', categoria: '', note: '',
  modalita_pagamento_id: '',
};

export default function FornitoriPage() {
  const [searchParams] = useSearchParams();
  const [fornitori, setFornitori] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [pivaInput, setPivaInput] = useState('');
  const [lookupBusy, setLookupBusy] = useState(false);
  const [modalita, setModalita] = useState([]);

  // OCR Listino dialog
  const [listinoOpen, setListinoOpen] = useState(false);
  const [listinoFornitore, setListinoFornitore] = useState(null);

  const openListino = (f) => {
    setListinoFornitore(f);
    setListinoOpen(true);
  };

  useEffect(() => {
    axios.get(`${API}/modalita-pagamento`, { withCredentials: true })
      .then(r => setModalita(r.data || []))
      .catch(() => {});
  }, []);

  const handleLookupPiva = async () => {
    const cleaned = pivaInput.replace(/[^0-9]/g, '');
    if (!cleaned || cleaned.length < 11) {
      toast.error('Inserisci una P.IVA valida (11 cifre)');
      return;
    }
    setLookupBusy(true);
    try {
      const res = await axios.get(`${API}/lookup-piva/${cleaned}`, { withCredentials: true });
      if (res.data?.valid && res.data?.ragione_sociale) {
        // Compila il form con i dati trovati
        setForm(prev => ({
          ...prev,
          ragione_sociale: res.data.ragione_sociale || prev.ragione_sociale,
          partita_iva: res.data.partita_iva_completa || `IT${res.data.partita_iva}`,
          indirizzo: res.data.indirizzo || prev.indirizzo,
          citta: res.data.citta || prev.citta,
          cap: res.data.cap || prev.cap,
          provincia: res.data.provincia || prev.provincia,
          codice_sdi: res.data.sdi_code || prev.codice_sdi,
        }));
        const label = getPivaSourceLabel(res.data.source);
        toast.success(`Trovato: ${res.data.ragione_sociale} (${label})`);
      } else if (res.data?.valid) {
        setForm(prev => ({ ...prev, partita_iva: res.data.partita_iva_completa || `IT${res.data.partita_iva}` }));
        toast.info(res.data?.message || 'P.IVA valida. Compila manualmente i dati.', { duration: 5000 });
      } else {
        toast.error(res.data?.message || 'P.IVA non valida');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore nel lookup P.IVA');
    } finally {
      setLookupBusy(false);
    }
  };

  const fetchFornitori = async () => {
    try {
      const r = await axios.get(`${API}/fornitori`, { withCredentials: true });
      setFornitori(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento fornitori');
    }
  };
  useEffect(() => { fetchFornitori(); }, []);

  // Filtro pre-applicato da querystring ?piva=...  ?q=...
  useEffect(() => {
    const piva = searchParams.get('piva');
    const q = searchParams.get('q');
    if (piva) setSearch(piva);
    else if (q) setSearch(q);
  }, [searchParams]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return fornitori;
    return fornitori.filter(f =>
      (f.ragione_sociale || '').toLowerCase().includes(s) ||
      (f.partita_iva || '').toLowerCase().includes(s) ||
      (f.email || '').toLowerCase().includes(s) ||
      (f.citta || '').toLowerCase().includes(s)
    );
  }, [fornitori, search]);

  const setField = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const openCreate = () => { setEditing(null); setForm(EMPTY); setPivaInput(''); setOpen(true); };
  const openEdit = (f) => { setEditing(f); setForm({ ...EMPTY, ...f }); setPivaInput(''); setOpen(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.ragione_sociale.trim()) { toast.error('Ragione sociale obbligatoria'); return; }
    setBusy(true);
    try {
      if (editing) {
        await axios.put(`${API}/fornitori/${editing.id}`, form, { withCredentials: true });
        toast.success('Fornitore aggiornato');
      } else {
        await axios.post(`${API}/fornitori`, form, { withCredentials: true });
        toast.success('Fornitore creato');
      }
      setOpen(false);
      fetchFornitori();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Eliminare il fornitore "${f.ragione_sociale}"?`)) return;
    try {
      await axios.delete(`${API}/fornitori/${f.id}`, { withCredentials: true });
      toast.success('Fornitore eliminato');
      fetchFornitori();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Building2 className="h-7 w-7 text-blue-600" />Anagrafica Fornitori</h1>
          <p className="text-slate-500 mt-1">{fornitori.length} fornitori registrati. Usali nei DDT e nelle fatture ricevute.</p>
        </div>
        <Button onClick={openCreate} className="bg-slate-900 text-white" data-testid="create-fornitore"><Plus className="h-4 w-4 mr-2" />Nuovo fornitore</Button>
      </div>

      <Card>
        <CardContent className="p-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per ragione sociale, P.IVA, città…" className="pl-10" data-testid="search-fornitori" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Ragione sociale</TableHead>
                <TableHead>P.IVA</TableHead>
                <TableHead>Città</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Contatti</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">
                  {fornitori.length === 0 ? 'Nessun fornitore — crea il primo' : 'Nessun risultato'}
                </TableCell></TableRow>
              ) : filtered.map(f => (
                <TableRow key={f.id} data-testid={`fornitore-${f.id}`}>
                  <TableCell className="font-medium">{f.ragione_sociale}</TableCell>
                  <TableCell className="font-mono text-xs">{f.partita_iva || '-'}</TableCell>
                  <TableCell className="text-sm">{f.citta ? `${f.citta}${f.provincia ? ' (' + f.provincia + ')' : ''}` : '-'}</TableCell>
                  <TableCell>{f.categoria ? <Badge variant="outline" className="text-[10px]">{f.categoria}</Badge> : '-'}</TableCell>
                  <TableCell className="text-xs text-slate-600 leading-tight">
                    {f.email && <div>{f.email}</div>}
                    {f.telefono && <div>{f.telefono}</div>}
                    {!f.email && !f.telefono && <span className="text-slate-400">-</span>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-purple-600 hover:bg-purple-50" onClick={() => openListino(f)} title="Importa listino prezzi con AI" data-testid={`forn-listino-${f.id}`}>
                      <ScanLine className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(f)} title="Modifica"><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => handleDelete(f)} title="Elimina"><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica' : 'Nuovo'} fornitore</DialogTitle>
            <DialogDescription>I dati saranno disponibili come autocomplete nei DDT e nelle fatture ricevute.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            {/* Lookup automatico P.IVA → compila i campi sotto */}
            <Card className="border-blue-200 bg-blue-50/40">
              <CardContent className="p-3">
                <Label className="text-xs font-semibold text-blue-900 flex items-center gap-1.5"><Search className="h-3.5 w-3.5" />Cerca automaticamente da P.IVA</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    placeholder="Es. 01234567890 o IT01234567890"
                    value={pivaInput}
                    onChange={(e) => setPivaInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLookupPiva(); } }}
                    maxLength={16}
                    className="font-mono bg-white"
                    data-testid="forn-piva-input"
                  />
                  <Button type="button" onClick={handleLookupPiva} disabled={lookupBusy} className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0" data-testid="forn-piva-lookup-btn">
                    {lookupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" />Cerca</>}
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">Recupera ragione sociale, indirizzo, città, CAP, provincia e codice SDI da Registro Imprese / VIES.</p>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Ragione sociale *</Label><Input value={form.ragione_sociale} onChange={setField('ragione_sociale')} required data-testid="form-rag-soc" /></div>
              <div><Label>Partita IVA</Label><Input value={form.partita_iva} onChange={setField('partita_iva')} className="font-mono" placeholder="IT12345678901" data-testid="form-piva" /></div>
              <div><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={setField('codice_fiscale')} className="font-mono" /></div>
              <div className="col-span-2"><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={setField('indirizzo')} /></div>
              <div><Label>Città</Label><Input value={form.citta} onChange={setField('citta')} /></div>
              <div className="grid grid-cols-2 gap-2"><div><Label>CAP</Label><Input value={form.cap} onChange={setField('cap')} /></div><div><Label>Prov.</Label><Input value={form.provincia} onChange={setField('provincia')} maxLength={2} /></div></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={setField('email')} /></div>
              <div><Label>PEC</Label><Input type="email" value={form.pec} onChange={setField('pec')} /></div>
              <div><Label>Telefono</Label><Input value={form.telefono} onChange={setField('telefono')} /></div>
              <div><Label>Codice SDI</Label><Input value={form.codice_sdi} onChange={setField('codice_sdi')} className="font-mono" maxLength={7} /></div>
              <div><Label>Referente commerciale</Label><Input value={form.referente} onChange={setField('referente')} /></div>
              <div><Label>Categoria</Label><Input value={form.categoria} onChange={setField('categoria')} placeholder="materiale_elettrico, allarmi…" /></div>
              <div className="col-span-2"><Label>IBAN</Label><Input value={form.iban} onChange={setField('iban')} className="font-mono" /></div>
              <div className="col-span-2">
                <Label>Modalità di pagamento predefinita</Label>
                <Select value={form.modalita_pagamento_id || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, modalita_pagamento_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="form-modalita"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">Nessuna (default sistema)</SelectItem>
                    {modalita.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">Quando registri un DDT da questo fornitore, scadenza pagamento e modalità verranno compilate in automatico.</p>
              </div>
              <div className="col-span-2"><Label>Note</Label><Input value={form.note} onChange={setField('note')} /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={busy} className="bg-slate-900 text-white" data-testid="form-save">{editing ? 'Salva' : 'Crea'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* OCR Listino dialog */}
      {listinoFornitore && (
        <ListinoOcrDialog
          open={listinoOpen}
          onOpenChange={(v) => { setListinoOpen(v); if (!v) setListinoFornitore(null); }}
          fornitore={listinoFornitore}
        />
      )}
    </div>
  );
}
